/**
 * "Send the link again", paced. See resend.ts for the numbers and why.
 *
 * Mounted once the first link has gone; that send is counted as the first,
 * and a page reload does not forget it (sessionStorage, per address).
 */

import { useEffect, useState } from 'react';
import { readRecord, resendState, writeRecord, type ResendRecord } from './resend';

interface Props {
  email: string;
  /** Ask the backend for another link. Errors are shown, not swallowed. */
  send: () => Promise<unknown>;
  /** The class for the button; the surrounding text is unstyled inline. */
  buttonClassName: string;
  className?: string;
}

function initialRecord(email: string): ResendRecord {
  // The first link went just before this mounted. A record kept from
  // earlier in this visit (a reload) carries its count forward.
  const kept = readRecord(email);
  const record = kept ? { sends: kept.sends + 1, lastSentAt: Date.now() } : { sends: 1, lastSentAt: Date.now() };
  writeRecord(email, record);
  return record;
}

export function ResendLink({ email, send, buttonClassName, className }: Props) {
  const [record, setRecord] = useState<ResendRecord>(() => initialRecord(email));
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const state = resendState(record, now);

  // Tick only while there is a count-down to show.
  useEffect(() => {
    if (state.kind !== 'wait') return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [state.kind]);

  async function again() {
    if (state.kind !== 'ready' || busy) return;
    setBusy(true);
    setNote(null);
    try {
      await send();
      const next = { sends: record.sends + 1, lastSentAt: Date.now() };
      writeRecord(email, next);
      setRecord(next);
      setNow(Date.now());
      setNote('Sent again. Give it a minute, and check your spam folder.');
    } catch {
      setNote('That did not go through. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={className}>
      {note && <p role="status" style={{ margin: '0 0 var(--sp-2)' }}>{note}</p>}
      {state.kind === 'exhausted' ? (
        <p style={{ margin: 0 }}>
          That is the limit for now. Wait 15 minutes and ask again, check your spam folder, or ask your administrator to confirm your account is active.
        </p>
      ) : state.kind === 'wait' ? (
        <p style={{ margin: 0 }}>
          Nothing yet? You can ask for the link again in {state.seconds} {state.seconds === 1 ? 'second' : 'seconds'}.
        </p>
      ) : (
        <p style={{ margin: 0 }}>
          Nothing yet?{' '}
          <button type="button" className={buttonClassName} onClick={() => void again()} disabled={busy} style={{ marginTop: 0 }}>
            {busy ? 'Sending…' : 'Send the link again'}
          </button>
          {state.remaining < MAX_RESENDS_LABEL_THRESHOLD && (
            <span> ({state.remaining} {state.remaining === 1 ? 'more time' : 'more times'})</span>
          )}
        </p>
      )}
    </div>
  );
}

/** Show the remaining count only once it is getting low. */
const MAX_RESENDS_LABEL_THRESHOLD = 3;
